// src/services/UnifiedApiService.ts
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { 
  Owner, 
  Fee, 
  Payment, 
  Expense, 
  Condo,
  User as CondoUser,
  CondoDocument, 
  DashboardStats,
  OwnerDashboard,
  QuarterlyReport,
  Statement,
  Revenue
} from '../pages/condoManagement/types/index'; 

import {
  Message,
  Appointment,
  LabResult,
  Prescription,
  SystemSettingsPayload
} from '../pages/medTech/types/index'

// ====================== TYPE DEFINITIONS ======================
export type AppType = 'medical' | 'condo' | 'tenant' | 'general';
export type ApiEndpoint = string;

export interface ApiConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
}

export interface AuthResponse {
  token: string;
  user: any;
  appType: AppType;
  permissions?: string[];
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

// ====================== UNIFIED API SERVICE ======================
class UnifiedApiService {
  private static instance: UnifiedApiService;
  private axiosInstances: Map<AppType, AxiosInstance> = new Map();
  private defaultConfigs: Map<AppType, ApiConfig> = new Map();
  private currentApp: AppType | null = null;

  private constructor() {
    this.initializeDefaultConfigs();
  }

  static getInstance(): UnifiedApiService {
    if (!UnifiedApiService.instance) {
      UnifiedApiService.instance = new UnifiedApiService();
    }
    return UnifiedApiService.instance;
  }

  // ====================== INITIALIZATION ======================
  private initializeDefaultConfigs(): void {
    this.defaultConfigs.set('medical', {
      baseURL: process.env.REACT_APP_MEDICAL_API_URL || 'http://localhost:8080/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.defaultConfigs.set('condo', {
      baseURL: process.env.REACT_APP_CONDO_API_URL || 'http://localhost:8085/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.defaultConfigs.set('tenant', {
      baseURL: process.env.REACT_APP_TENANT_API_URL || 'http://localhost:8090/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.defaultConfigs.set('general', {
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // ====================== AXIOS INSTANCE MANAGEMENT ======================
  private getAxiosInstance(appType: AppType): AxiosInstance {
    if (!this.axiosInstances.has(appType)) {
      const config = this.defaultConfigs.get(appType) || this.defaultConfigs.get('general')!;
      const instance = axios.create(config);
      
      // Request interceptor
      instance.interceptors.request.use((config) => {
        const token = this.getToken(appType);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['X-App-Type'] = appType;
        return config;
      });

      // Response interceptor
      instance.interceptors.response.use(
        (response) => response,
        (error) => {
          if (error.response?.status === 401) {
            this.handleUnauthorized(appType);
          }
          return Promise.reject(this.normalizeError(error));
        }
      );

      this.axiosInstances.set(appType, instance);
    }

    return this.axiosInstances.get(appType)!;
  }

  // ====================== TOKEN MANAGEMENT ======================
  // Sprint 110 (110.1) — access tokens live IN MEMORY only, never in
  // localStorage/sessionStorage (an XSS payload could otherwise read the bearer
  // token). Keyed per app-type so the unified service can hold tokens for more
  // than one vertical concurrently. Lost on reload by design; the per-vertical
  // auth flow re-establishes them (the condo app re-mints from its HttpOnly
  // refresh cookie via the condo httpClient bootstrap).
  private tokensInMemory: Map<AppType, string> = new Map();
  private usersInMemory: Map<AppType, string> = new Map();

  private getToken(appType: AppType): string | null {
    return this.tokensInMemory.get(appType) ?? null;
  }

  private setToken(appType: AppType, token: string): void {
    this.tokensInMemory.set(appType, token);
  }

  private removeToken(appType: AppType): void {
    this.tokensInMemory.delete(appType);
    this.usersInMemory.delete(appType);
    // Defensive: purge any access token / user blob a prior build persisted.
    try {
      localStorage.removeItem(`unified_auth_${appType}_token`);
      localStorage.removeItem(`unified_auth_${appType}_user`);
    } catch { /* best effort */ }
  }

  // ====================== CORE API METHODS ======================
  async request<T>(
    appType: AppType,
    endpoint: string,
    config: AxiosRequestConfig = {}
  ): Promise<T> {
    const instance = this.getAxiosInstance(appType);
    this.currentApp = appType;

    try {
      const response: AxiosResponse<T> = await instance({
        url: endpoint,
        ...config,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async unauthenticatedRequest<T>(
    appType: AppType,
    endpoint: string,
    data?: any,
    config: AxiosRequestConfig = {}
  ): Promise<T> {
    // Create a clean instance without auth interceptor for this request
    const cleanInstance = axios.create({
      baseURL: this.defaultConfigs.get(appType)?.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'X-App-Type': appType,
      },
    });

    try {
      const response = await cleanInstance.request<T>({
        url: endpoint,
        data,
        ...config,
      });
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  // ====================== AUTHENTICATION METHODS ======================
  async loginApi(appType: AppType, email: string, password: string): Promise<AuthResponse> {
    const endpoint = '/auth/login';
    const payload = { email, password };

    try {
      const response = await this.unauthenticatedRequest<AuthResponse>(
        appType,
        endpoint,
        payload,
        { method: 'POST' }
      );

      if (response.token && response.user) {
        // Sprint 110 (110.1) — token + user held in memory only, not localStorage.
        this.setToken(appType, response.token);
        this.usersInMemory.set(appType, JSON.stringify(response.user));
      }

      return response;
    } catch (error) {
      throw this.handleAuthError(error as ApiError);
    }
  }

  async getMe(appType: AppType): Promise<any> {
    const endpoint = '/auth/me';
    return this.request<any>(appType, endpoint);
  }

  async register(
    appType: AppType,
    userData: any
  ): Promise<AuthResponse> {
    const endpoint = '/auth/register';
    
    return this.request<AuthResponse>(appType, endpoint, {
      method: 'POST',
      data: userData,
    });
  }

  async logout(appType: AppType): Promise<void> {
    try {
      await this.request<void>(appType, '/auth/logout', { method: 'POST' });
    } finally {
      this.removeToken(appType);
    }
  }

  // ====================== MEDICAL APP SPECIFIC METHODS ======================
//   // Auth methods for medical app
//   async medicalLogin(email: string, password: string): Promise<AuthResponse> {
//     return this.loginApi('medical', email, password);
//   }

  // Messages
  async getMessages(): Promise<Message[]> {
    return this.request<Message[]>('medical', '/messages');
  }

  async sendMessage(message: { recipientId: string; subject: string; body: string }): Promise<Message> {
    return this.request<Message>('medical', '/messages', {
      method: 'POST',
      data: message,
    });
  }

  // Appointments
  async getAppointments(): Promise<Appointment[]> {
    return this.request<Appointment[]>('medical', '/appointments');
  }

  async createAppointment(appointment: Omit<Appointment, 'id' | 'createdAt'>): Promise<Appointment> {
    return this.request<Appointment>('medical', '/appointments', {
      method: 'POST',
      data: appointment,
    });
  }

  // Lab Results
  async getLabResults(): Promise<LabResult[]> {
    return this.request<LabResult[]>('medical', '/lab-results');
  }

  async getLabResult(id: string): Promise<LabResult> {
    return this.request<LabResult>('medical', `/lab-results/${id}`);
  }

  async fetchAdminLabResults(): Promise<LabResult[]> {
    try {
      return await this.request<LabResult[]>('medical', '/admin/lab-results');
    } catch (error) {
      console.warn('Using mock lab results due to API error:', error);
      return this.getMockLabResults();
    }
  }

  // Prescriptions
  async getPrescriptions(patientId: string): Promise<Prescription[]> {
    return this.request<Prescription[]>('medical', `/patients/${patientId}/prescriptions`);
  }

  // System Settings
  async saveSystemSettings(payload: SystemSettingsPayload): Promise<void> {
    return this.request<void>('medical', '/admin/system-settings', {
      method: 'PUT',
      data: payload,
    });
  }

  async resetUserPassword(userId: string): Promise<void> {
    return this.request<void>('medical', `/admin/users/${userId}/reset-password`, {
      method: 'POST',
    });
  }

  // ====================== CONDO APP SPECIFIC METHODS ======================
  // Condos
  async getCondos(): Promise<Condo[]> {
    return this.request<Condo[]>('condo', '/condos/available');
  }

  async getCondo(id: string): Promise<Condo> {
    return this.request<Condo>('condo', `/condos/${id}`);
  }

  async createCondo(condo: Omit<Condo, 'id' | 'createdAt' | 'updatedAt'>): Promise<Condo> {
    return this.request<Condo>('condo', '/condos', {
      method: 'POST',
      data: condo,
    });
  }

  async updateCondo(id: string, condo: Partial<Condo>): Promise<Condo> {
    return this.request<Condo>('condo', `/condos/${id}`, {
      method: 'PUT',
      data: condo,
    });
  }

  async deleteCondo(id: string): Promise<void> {
    return this.request<void>('condo', `/condos/${id}`, {
      method: 'DELETE',
    });
  }

  // Owners
  async getOwners(condoId?: string): Promise<Owner[]> {
    const endpoint = condoId ? `/owners/condo/${condoId}` : '/owners';
    return this.request<Owner[]>('condo', endpoint);
  }

  async getOwner(id: string): Promise<Owner> {
    return this.request<Owner>('condo', `/owners/${id}`);
  }

  async createOwner(owner: Omit<Owner, 'id'>): Promise<Owner> {
    return this.request<Owner>('condo', '/owners', {
      method: 'POST',
      data: owner,
    });
  }

  async updateOwner(id: string, owner: Partial<Owner>): Promise<Owner> {
    return this.request<Owner>('condo', `/owners/${id}`, {
      method: 'PUT',
      data: owner,
    });
  }

  async deleteOwner(id: string): Promise<void> {
    return this.request<void>('condo', `/owners/${id}`, {
      method: 'DELETE',
    });
  }

  // Fees
  async getFees(condoId?: string): Promise<Fee[]> {
    // Sprint 81 (ADR-081) — the legacy global `/fees?condoId=` path was locked
    // down by the Sprint 61/76 tenancy hardening (403 for OWNER, masked as
    // $0.00). The scoped `/fees/condo/{condoId}` path is the one the lockdown
    // left open and must be used. Bare `/fees` has no tenant scope and 403s;
    // callers that can't supply condoId are a Phase B concern.
    const endpoint = condoId ? `/fees/condo/${condoId}` : '/fees';
    return this.request<Fee[]>('condo', endpoint);
  }

  async getFee(id: string): Promise<Fee> {
    return this.request<Fee>('condo', `/fees/${id}`);
  }

  async createFee(fee: Omit<Fee, 'id'>): Promise<Fee> {
    return this.request<Fee>('condo', '/fees', {
      method: 'POST',
      data: fee,
    });
  }

  async updateFee(id: string, fee: Partial<Fee>): Promise<Fee> {
    return this.request<Fee>('condo', `/fees/${id}`, {
      method: 'PUT',
      data: fee,
    });
  }

  async deleteFee(id: string): Promise<void> {
    return this.request<void>('condo', `/fees/${id}`, {
      method: 'DELETE',
    });
  }

  // Payments
  async getPayments(condoId?: string): Promise<Payment[]> {
    const endpoint = condoId ? `/payments?condoId=${condoId}` : '/payments';
    return this.request<Payment[]>('condo', endpoint);
  }

  async createPayment(payment: Omit<Payment, 'id'>): Promise<Payment> {
    return this.request<Payment>('condo', '/payments', {
      method: 'POST',
      data: payment,
    });
  }

  // Expenses
  async getExpenses(condoId?: string): Promise<Expense[]> {
    const endpoint = condoId ? `/expenses?condoId=${condoId}` : '/expenses';
    return this.request<Expense[]>('condo', endpoint);
  }

  async createExpense(expense: Omit<Expense, 'id'>): Promise<Expense> {
    return this.request<Expense>('condo', '/expenses', {
      method: 'POST',
      data: expense,
    });
  }

  // Revenue
  async getRevenues(condoId?: string): Promise<Revenue[]> {
    const endpoint = condoId ? `/revenues?condoId=${condoId}` : '/revenues';
    return this.request<Revenue[]>('condo', endpoint);
  }

  async createRevenue(revenue: Omit<Revenue, 'id'>): Promise<Revenue> {
    return this.request<Revenue>('condo', '/revenues', {
      method: 'POST',
      data: revenue,
    });
  }

  // Statements
  async getStatements(condoId?: string): Promise<Statement[]> {
    const endpoint = condoId ? `/statements?condoId=${condoId}` : '/statements';
    return this.request<Statement[]>('condo', endpoint);
  }

  async createStatement(statement: Omit<Statement, 'id'>): Promise<Statement> {
    return this.request<Statement>('condo', '/statements', {
      method: 'POST',
      data: statement,
    });
  }

  // Dashboard Stats
  async getDashboardStats(condoId?: string): Promise<DashboardStats> {
    const endpoint = condoId ? `/dashboard/stats?condoId=${condoId}` : '/dashboard/stats';
    return this.request<DashboardStats>('condo', endpoint);
  }

  async getDashboardOverview(condoId?: string): Promise<{
    totalRevenue: number;
    totalExpenses: number;
    netIncome: number;
    feeCollectionRate: number;
    occupancyRate: number;
    pendingFees: number;
    overdueFees: number;
  }> {
    const endpoint = condoId ? `/dashboard/overview?condoId=${condoId}` : '/dashboard/overview';
    return this.request('condo', endpoint);
  }

  async getFinancialSummary(condoId?: string): Promise<{
    currentQuarter: {
      revenue: number;
      expenses: number;
      netIncome: number;
    };
    previousQuarter: {
      revenue: number;
      expenses: number;
      netIncome: number;
    };
    yearToDate: {
      revenue: number;
      expenses: number;
      netIncome: number;
    };
  }> {
    const endpoint = condoId ? `/dashboard/financial-summary?condoId=${condoId}` : '/dashboard/financial-summary';
    return this.request('condo', endpoint);
  }

  async getCollectionMetrics(condoId?: string): Promise<{
    totalFees: number;
    collectedAmount: number;
    pendingAmount: number;
    overdueAmount: number;
    collectionRate: number;
    averageCollectionTime: number;
  }> {
    const endpoint = condoId ? `/dashboard/collection-metrics?condoId=${condoId}` : '/dashboard/collection-metrics';
    return this.request('condo', endpoint);
  }

  async getOwnerMetrics(condoId?: string): Promise<{
    totalOwners: number;
    activeOwners: number;
    newOwnersThisMonth: number;
    occupancyRate: number;
    averageTenure: number;
  }> {
    const endpoint = condoId ? `/dashboard/owner-metrics?condoId=${condoId}` : '/dashboard/owner-metrics';
    return this.request('condo', endpoint);
  }

  async getExpenseBreakdown(condoId?: string): Promise<{
    byCategory: Array<{
      category: string;
      amount: number;
      percentage: number;
    }>;
    byMonth: Array<{
      month: string;
      amount: number;
    }>;
    topVendors: Array<{
      vendor: string;
      amount: number;
    }>;
  }> {
    const endpoint = condoId ? `/dashboard/expense-breakdown?condoId=${condoId}` : '/dashboard/expense-breakdown';
    return this.request('condo', endpoint);
  }

  async getRevenueBreakdown(condoId?: string): Promise<{
    bySource: Array<{
      source: string;
      amount: number;
      percentage: number;
    }>;
    byMonth: Array<{
      month: string;
      amount: number;
    }>;
  }> {
    const endpoint = condoId ? `/dashboard/revenue-breakdown?condoId=${condoId}` : '/dashboard/revenue-breakdown';
    return this.request('condo', endpoint);
  }

  async getRecentActivity(condoId?: string): Promise<{
    recentPayments: Array<{
      id: string;
      ownerName: string;
      amount: number;
      date: string;
      method: string;
    }>;
    recentExpenses: Array<{
      id: string;
      description: string;
      amount: number;
      date: string;
      vendor?: string;
    }>;
    upcomingFees: Array<{
      id: string;
      ownerName: string;
      amount: number;
      dueDate: string;
    }>;
  }> {
    const endpoint = condoId ? `/dashboard/recent-activity?condoId=${condoId}` : '/dashboard/recent-activity';
    return this.request('condo', endpoint);
  }

  async getMaintenanceMetrics(condoId?: string): Promise<{
    openRequests: number;
    completedThisMonth: number;
    averageResolutionTime: number;
    byPriority: Array<{
      priority: string;
      count: number;
    }>;
    byStatus: Array<{
      status: string;
      count: number;
    }>;
  }> {
    const endpoint = condoId ? `/dashboard/maintenance-metrics?condoId=${condoId}` : '/dashboard/maintenance-metrics';
    return this.request('condo', endpoint);
  }

  // Quarterly Reports
  async getQuarterlyReport(quarter: number, year: number, condoId?: string): Promise<QuarterlyReport> {
    const endpoint = condoId 
      ? `/reports/quarterly?quarter=${quarter}&year=${year}&condoId=${condoId}`
      : `/reports/quarterly?quarter=${quarter}&year=${year}`;
    return this.request<QuarterlyReport>('condo', endpoint);
  }

  // Owner Dashboard
  async getOwnerDashboard(ownerId: string): Promise<OwnerDashboard> {
    return this.request<OwnerDashboard>('condo', `/dashboard/owner/${ownerId}`);
  }

  // Documents
  async getDocuments(condoId?: string): Promise<CondoDocument[]> {
    const endpoint = condoId ? `/documents?condoId=${condoId}` : '/documents';
    return this.request<CondoDocument[]>('condo', endpoint);
  }

  async uploadDocument(formData: FormData): Promise<CondoDocument> {
    const token = this.getToken('condo');
    const response = await axios.post(
      `${this.defaultConfigs.get('condo')?.baseURL}/documents/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` }),
          'X-App-Type': 'condo',
        },
      }
    );
    return response.data;
  }

  // Send statement email
  async sendStatementEmail(statementId: string): Promise<void> {
    return this.request<void>('condo', `/statements/${statementId}/send`, {
      method: 'POST',
    });
  }

  // Send bulk statements
  async sendBulkStatements(period: string, condoId?: string): Promise<{ statementId: string; success: boolean; error?: string }[]> {
    const endpoint = condoId 
      ? `/statements/bulk-send?period=${period}&condoId=${condoId}`
      : `/statements/bulk-send?period=${period}`;
    return this.request('condo', endpoint, {
      method: 'POST',
    });
  }

  // User Profile
  async updateUserProfile(
    appType: AppType,
    userData: Partial<CondoUser> & { currentPassword?: string; newPassword?: string }
  ): Promise<CondoUser> {
    return this.request<CondoUser>(appType, '/users/profile', {
      method: 'PUT',
      data: userData,
    });
  }

  // ====================== CROSS-APP METHODS ======================
  async switchApp(currentApp: AppType, targetApp: AppType): Promise<boolean> {
    try {
      // Validate user has access to target app
      const canSwitch = await this.validateAppAccess(currentApp, targetApp);
      
      if (canSwitch) {
        // Transfer session if needed
        await this.transferSession(currentApp, targetApp);
        this.currentApp = targetApp;
        return true;
      }
      return false;
    } catch (error) {
      console.error('App switch failed:', error);
      return false;
    }
  }

  async getAppPermissions(appType: AppType): Promise<string[]> {
    return this.request<string[]>(appType, '/auth/permissions');
  }

  async getAvailableApps(): Promise<AppType[]> {
    try {
      const response = await this.request<{ apps: AppType[] }>('general', '/auth/apps');
      return response.apps;
    } catch {
      // Sprint 110 (110.1) — fall back to the in-memory token map (no
      // localStorage token lookup).
      const apps: AppType[] = [];
      (['medical', 'condo', 'tenant', 'general'] as AppType[]).forEach((app) => {
        if (this.tokensInMemory.has(app)) {
          apps.push(app);
        }
      });
      return apps;
    }
  }

  // ====================== UTILITY METHODS ======================
  private normalizeError(error: any): ApiError {
    if (axios.isAxiosError(error)) {
      return {
        message: error.response?.data?.message || error.message || 'Network error',
        code: error.response?.data?.code,
        status: error.response?.status,
      };
    }
    return {
      message: error.message || 'Unknown error',
    };
  }

  private handleUnauthorized(appType: AppType): void {
    this.removeToken(appType);
    
    // Only redirect if this was the current app
    if (this.currentApp === appType) {
      window.location.href = `/login?app=${appType}&reason=session_expired`;
    }
  }

  private handleAuthError(error: ApiError): ApiError {
    const message = error.message?.toLowerCase();
    
    if (message.includes('401') || 
        message.includes('invalid') || 
        message.includes('unauthorized')) {
      return {
        ...error,
        message: 'Incorrect username or password',
      };
    }
    
    return error;
  }

  private async validateAppAccess(currentApp: AppType, targetApp: AppType): Promise<boolean> {
    try {
      const response = await this.request<{ allowed: boolean }>(
        'general',
        `/auth/validate-app?source=${currentApp}&target=${targetApp}`
      );
      return response.allowed;
    } catch {
      // Fallback: check if user has token for target app
      return !!this.getToken(targetApp);
    }
  }

  private async transferSession(sourceApp: AppType, targetApp: AppType): Promise<void> {
    // In a real implementation, this would exchange tokens between services
    console.log(`Transferring session from ${sourceApp} to ${targetApp}`);
  }

  // Mock data for development
  private getMockLabResults(): LabResult[] {
    return [
      {
        id: '1',
        patientId: 'patient-1',
        patientName: 'John Smith',
        providerId: 'provider-1',
        providerName: 'Dr. Sarah Johnson',
        testName: 'Complete Blood Count (CBC)',
        testDate: '2024-01-15T10:00:00Z',
        resultValue: '5.2',
        resultUnit: 'million/mL',
        referenceRange: '4.5–5.9 million/mL',
        interpretation: 'normal',
        notes: 'Within normal limits.',
        createdAt: '2024-01-15T12:00:00Z',
        status: 'reviewed'
      },
      {
        id: '2',
        patientId: 'patient-2',
        patientName: 'Emma Wilson',
        providerId: 'provider-2',
        providerName: 'Dr. Michael Lee',
        testName: 'Hemoglobin A1c',
        testDate: '2024-01-12T09:00:00Z',
        resultValue: '8.4',
        resultUnit: '%',
        referenceRange: '4.0–5.6%',
        interpretation: 'critical',
        notes: 'Immediate follow-up required.',
        createdAt: '2024-01-12T11:00:00Z',
        status: 'pending'
      },
      {
        id: '3',
        patientId: 'patient-3',
        patientName: 'Robert Chen',
        providerId: 'provider-3',
        providerName: 'Dr. Alicia Gomez',
        testName: 'Cholesterol Panel',
        testDate: '2024-01-10T08:30:00Z',
        resultValue: '265',
        resultUnit: 'mg/dL',
        referenceRange: '<200 mg/dL',
        interpretation: 'abnormal',
        notes: 'Dietary and medication changes recommended.',
        createdAt: '2024-01-10T10:00:00Z',
        status: 'completed'
      }
    ];
  }

  // ====================== PUBLIC UTILITY METHODS ======================
  isAuthenticated(appType: AppType): boolean {
    return !!this.getToken(appType);
  }

  getCurrentApp(): AppType | null {
    return this.currentApp;
  }

  clearAllSessions(): void {
    ['medical', 'condo', 'tenant', 'general'].forEach((app) => {
      this.removeToken(app as AppType);
    });
    this.currentApp = null;
  }

  // Helper to get the appropriate API service for different contexts
  forApp(appType: AppType) {
    return {
      // Auth
      loginApi: (email: string, password: string) => this.loginApi(appType, email, password),
      getMe: () => this.getMe(appType),
      logout: () => this.logout(appType),

      // Utility methods
    isAuthenticated: () => this.isAuthenticated(appType),
      
      // Generic request
      request: <T>(endpoint: string, config?: AxiosRequestConfig) => 
        this.request<T>(appType, endpoint, config),
      
      // App-specific shortcuts
      ...(appType === 'medical' ? {
        getMessages: () => this.getMessages(),
        register:(appType: AppType, userData: any) => this.register(appType, userData),
        getAppointments: () => this.getAppointments(),
        getLabResults: () => this.getLabResults(),
        fetchAdminLabResults: () => this.fetchAdminLabResults(),
        saveSystemSettings: (payload: SystemSettingsPayload) => this.saveSystemSettings(payload),
        resetUserPassword: (userId: string) => this.resetUserPassword(userId),
      } : {}),
      
      ...(appType === 'condo' ? {
        getCondos: () => this.getCondos(),
        register:(appType: AppType, userData: any) => this.register(appType, userData),
        getOwners: (condoId?: string) => this.getOwners(condoId),
        getFees: (condoId?: string) => this.getFees(condoId),
        getPayments: (condoId?: string) => this.getPayments(condoId),
        getExpenses: (condoId?: string) => this.getExpenses(condoId),
        getDashboardStats: (condoId?: string) => this.getDashboardStats(condoId),
        uploadDocument: (formData: FormData) => this.uploadDocument(formData),
        updateUserProfile: (userData: Partial<CondoUser> & { currentPassword?: string; newPassword?: string }) => 
          this.updateUserProfile(appType, userData),
      } : {}),
    };
  }
}

// ====================== ZUSTAND STORE COMPATIBILITY ======================
// Create a hook that mimics the original useApiService


export const useApiService = (appType?: AppType) => {
  const apiService = UnifiedApiService.getInstance();
  
  // If no appType provided, return the service with a default app context
  if (!appType) {
    return {
      // Direct access to methods with appType parameter
      loginApi: (appType: AppType, email: string, password: string) => 
        apiService.loginApi(appType, email, password),
      getMe: (appType: AppType) => apiService.getMe(appType),
      isAuthenticated: (appType: AppType) => apiService.isAuthenticated(appType),  
      updateUserProfile : (appType: AppType, userData: any) => apiService.updateUserProfile(appType, userData),
      register :  (appType: AppType, userData: any) => apiService.register(appType, userData),
      
      // For app-specific contexts
      forApp: (appType: AppType) => apiService.forApp(appType),
      
      // Cross-app methods
      switchApp: (currentApp: AppType, targetApp: AppType) => 
        apiService.switchApp(currentApp, targetApp),
      getAvailableApps: () => apiService.getAvailableApps(),
      clearAllSessions: () => apiService.clearAllSessions(),
    };
  }
  
  // Return service for specific app
  return apiService.forApp(appType);
};

// Alternative: Simple hook that returns the full service
export const useUnifiedApiService = () => {
  return UnifiedApiService.getInstance();
};

// ====================== LEGACY COMPATIBILITY ======================
// For backward compatibility with existing code
export const apiService = UnifiedApiService.getInstance();

// Medical app legacy exports
export const authAPI = {
  login: (email: string, password: string) => 
    apiService.loginApi('medical',email, password),
  register: (appType: AppType, userData: any) => 
    apiService.register(appType, userData),
  logout: () => apiService.logout('medical'),
  updateUserProfile: (appType: AppType, userData: any) => apiService.updateUserProfile(appType, userData),
};

export const messagesAPI = {
  getMessages: () => apiService.getMessages(),
  sendMessage: (message: { recipientId: string; subject: string; body: string }) =>
    apiService.sendMessage(message),
};

export const appointmentsAPI = {
  getAppointments: () => apiService.getAppointments(),
  createAppointment: (appointment: Omit<Appointment, 'id' | 'createdAt'>) =>
    apiService.createAppointment(appointment),
};

export const labResultsAPI = {
  getLabResults: () => apiService.getLabResults(),
  getLabResult: (id: string) => apiService.getLabResult(id),
  fetchAdminLabResults: () => apiService.fetchAdminLabResults(),
};

export const patientAPI = {
  getPrescriptions: (patientId: string) => apiService.getPrescriptions(patientId),
};

export const systemAPI = {
  saveSystemSettings: (payload: SystemSettingsPayload) => 
    apiService.saveSystemSettings(payload),
  resetUserPassword: (userId: string) => apiService.resetUserPassword(userId),
};

// Condo app legacy exports
export const condoAPI = {
  register:(appType: AppType, userData: any) => apiService.register(appType, userData),
  getCondos: () => apiService.getCondos(),
  getOwners: (condoId?: string) => apiService.getOwners(condoId),
  getFees: (condoId?: string) => apiService.getFees(condoId),
  getPayments: (condoId?: string) => apiService.getPayments(condoId),
  getDashboardStats: (condoId?: string) => apiService.getDashboardStats(condoId),
  uploadDocument: (formData: FormData) => apiService.uploadDocument(formData),
  updateUserProfile: (userData: any) => apiService.updateUserProfile('condo', userData),
};

// Utility functions for legacy code
export const isAuthenticated = (appType: AppType = 'medical'): boolean => {
  return apiService.isAuthenticated(appType);
};

export const getCurrentUser = (appType: AppType = 'medical'): any => {
  const userStr = localStorage.getItem(`unified_auth_${appType}_user`);
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
};

// Export the singleton instance
export default UnifiedApiService.getInstance();